import { ObjectStorageIcon as ResourceIcon } from "@netapp/bxp-style/react-icons/Storage";
import _ from "lodash";
import { findPathToScope, getTagValue, findPathToScopeRoles } from "modules/identity-access-management/utils";
import { Link } from "react-router-dom";
import { showAIQInIAM } from "utils/localstorage";
import { isNcl } from "utils/app/utils";
import * as yup from 'yup';
import styles from "../Settings.module.scss";
import { resourceTypes } from "./ResourceTypes";

export const DEFAULT_SORT_VALUE = { column: 'name', sortOrder: 'desc' };
const FILTER_OPERATOR = {AND: ' and ',OR: ' or ',NOT_EQUAL:' ne ',EQUAL: ' eq '};
export const DEFAULT_FILTER_VALUE = { columns: { ownerOrganizationId: { values: { "ownerOrganizationId": true } } } };

export const showAIQInventory = showAIQInIAM();
export const AIQinventory = "resourceType eq 'cluster' and resourceClass eq 'inventory' and not (tags.internal:bxp:workingEnvironmentId ex) and (tags.internal:bxp:platformType ex)";
export const AIQquery = `(${AIQinventory} and (tags.internal:bxp:variantType eq 'HCI' or tags.internal:bxp:variantType eq 'ASTRA' or tags.internal:bxp:variantType eq 'ALTAVAULT' or tags.internal:bxp:variantType eq 'SOLIDFIRE' or tags.internal:bxp:variantType eq 'STORAGEGRID' or tags.internal:bxp:variantType eq 'E-SERIES' or tags.internal:bxp:variantType eq 'ONTAP' or tags.internal:bxp:variantType eq 'ONTAP-EDGE' or tags.internal:bxp:variantType eq 'CVO' or tags.internal:bxp:variantType eq 'CVOHA' or tags.internal:bxp:variantType eq 'CVS' or tags.internal:bxp:variantType eq 'ONTAP-SELECT'))`;
export const AIQqueryOnPrem = `(${AIQinventory} and tags.internal:bxp:platformType eq 'ONPREM')`;
export const AIQqueryAWS = `(${AIQinventory} and tags.internal:bxp:platformType eq 'AWS')`;;
export const AIQqueryAzure = `(${AIQinventory} and tags.internal:bxp:platformType eq 'AZURE')`;
export const AIQqueryGCP = `(${AIQinventory} and tags.internal:bxp:platformType eq 'GCP')`;
export const backupAWSQuery = 'resourceClass eq "backup" and tags.internal:bxp:platformType eq "AWS"';
export const backupAzureQuery = 'resourceClass eq "backup" and tags.internal:bxp:platformType eq "AZURE"';
export const backupGcpQuery = 'resourceClass eq "backup" and tags.internal:bxp:platformType eq "GCP"';
export const backupOnpremQuery = 'resourceClass eq "backup" and tags.internal:bxp:platformType eq "ONPREM"';

export const AIQqueryKeystone = `(resourceClass eq 'keystone')`;

// Build filter from resourceTypes data 
export const buildFilterFromResourceTypes = (resourceTypesData) => {
  if (!resourceTypesData || resourceTypesData.length === 0) {
    return null;
  }
  const seen = new Set();
  const parts = [];
  for (const { resourceClass, resourceType } of resourceTypesData) {
    const key = `${resourceClass}-${resourceType}`;
    if (!seen.has(key)) {
      seen.add(key);
      parts.push(`(resourceClass eq '${resourceClass}' and resourceType eq '${resourceType}')`);
    }
  }
  return parts.join(' or ');
};

export const getDefaultFilter = (resourceTypesData) => {
  if (isNcl()) {
    return "resourceType eq 'ON_PREM' or resourceType eq 'ESERIES' or resourceType eq 'SG' or resourceType in ('BR_MSSQL_HOST','BR_ORACLE_HOST','BR_VMWARE_VCENTER','BR_HYPERV_HOST','BR_KVM_HOST','BR_K8S_CLUSTER','BR_VOLUMES_SVM')";
  }
  
  const fullQuery = buildFilterFromResourceTypes(resourceTypesData);
  if (!fullQuery) {
    return null;
  }
  return showAIQInventory ? AIQquery + ' or ' + fullQuery : fullQuery;
};

// defaultFilter is no longer statically available - use getDefaultFilter(resourceTypesData) with API data
export const aiqTerms = (t) => {
 return {
  HCI: t('hci'),
  ASTRA: t('astra'),
  ALTAVAULT: t('altaVault'),
  SOLIDFIRE: t('solid_fire'),
  STORAGEGRID: t('storage_grid'),
  'E-SERIES': t('e_series'),
  ONTAP: t('on_prem_ontap_cluster'),
  'ONTAP-EDGE': t('ontap_edge'),
  CVO: t('cloud_volumes_ontap'),
  CVOHA: t('cloud_volumes_ha_pair'),
  CVS: t('cloud_volumes_service'),
  'ONTAP-SELECT': t('ontap_select')
 }
}

const getResourceMap = t => resourceTypes(t).reduce((acc, resource) => {
  const key = `${resource.resourceClass}-${resource.resourceType}`;
  acc[key] = resource;
  return acc;
}, {});

export const determineResourceType = (resource, t) => {
  const { resourceClass, resourceType, tags } = resource;
  const resourceMap = getResourceMap(t);
  const key = `${resourceClass}-${resourceType}`;

  if(resourceClass === "backup") {
    let cloudProvider = getTagValue(tags, "internal:bxp:platformType");
    return {...resourceMap[key], cloudProvider };
  }

  if (
    resourceClass === "STORAGE_SERVICES" &&
    resourceType === "VMWARE_VCENTER"
  ) {
    let cloudProvider = tags.find((tag) =>
      ["onPrem", "AWS"].includes(tag["internal:bxp:metadata:location"])
    );
    if (cloudProvider) {
      resourceMap[key].cloudProvider =
        cloudProvider["internal:bxp:metadata:location"].toUpperCase();
    } else {
      resourceMap[key].cloudProvider = "NA";
    }
  }
  if (
    resourceClass === "inventory" &&
    resourceType === "cluster"
  ) {
    let cloudProvider = getTagValue(tags, "internal:bxp:platformType");
    let type = getTagValue(tags, "internal:bxp:variantType");
    return {...resourceMap[key], cloudProvider, label: type}
  }

  if (
    resourceClass === "keystone" &&
    resourceType === "subscription"
  ) {
    let cloudProvider = "keystone";
    return {...resourceMap[key], cloudProvider, label: t('subscription')}
  }

  return resourceMap[key] || null;
};

/*
 * This function is used to find the each type of scopes for a resource.
 * @param {Array} tags - Tags of the resource
 * @param {String} id - Id of the resource
 * @param {Object} selectedOrg - selectedOrg data
 * output - { count: Number, scopeDetailsPageData: Array, scopeCount: Object }
 * count - Total count of scopes
 * scopeDetailsPageData - Array of scopes for the resource. scopeDetailsPageData having the key scopes.
 * */

function createScopeDetail(type, id, resourceId, scopes, scopeDetails) {
  return {
    type,
    resourceId,
    [`${type}Id`]: id,
    scopes,
    scopeDetails
  };
}

export const getUserAccessAndSummary = (
  scopeAccess,
  selectedOrg,
  roleMap,
  resourceId
) => {
  if(!scopeAccess?.items || scopeAccess?.items?.length === 0) {
    return {
        count: 0,
        scopeDetailsPageData: [],
    };
  }
  const scopeDetailsPageData = []
  const scopeAccessMap = {}
  for (const scope of scopeAccess.items) {
    scopeAccessMap[scope.id] = scope;
  }

  // determine scope details
  for (const scope of scopeAccess.items) {
    // find the path.
    const scopeDetails = findPathToScopeRoles([selectedOrg], scope.id, roleMap, scopeAccessMap)
    const scopes = _.map(scopeDetails, scope => scope?.name);
    

    if (scopeDetails.length > 0) {
      // scope on which role is set
      const lastScope = scopeDetails[scopeDetails.length -1]
      scopeDetailsPageData.push({
        scopes,
        scopeDetails,
        [`${lastScope.type}Id`]: lastScope.id,
        type: lastScope.type,
        resourceId,
        multiRoles: lastScope.multiRoles,
        isParnershipRole: lastScope.isParnershipRole
      })
    }
  }
  return {
        count: scopeDetailsPageData.length,
        scopeDetailsPageData,
    };
}

export const getResourceTypeCountsAndScopes = (
  tags,
  resourceId,
  selectedOrg,
  countAncestors = false
) => {
  if (!tags || tags.length === 0) {
    return {
      count: 0,
      scopeDetailsPageData: [],
      scopeCount: { projectCount: 0, folderCount: 0, organizationCount: 0 },
    };
  }
  const scopeDetailsPageRow = [];
  const count = tags.reduce(
    (acc, tag) => {
      const key = Object.keys(tag)[0];
      // Return acc if the key is undefined
      if(_.isEmpty(key)) {
        return acc;
      }
      const lastKeyType = getLastKeyAfterColon(key);
      let arr = [];
      // what if user is associated with multiple organization
      if (lastKeyType === "organizationId" && tag[key] !== selectedOrg.id) {
        return acc;
      }
      const scopeDetails =
        lastKeyType === "organizationId"
          ? findScopesForTypeOrg(selectedOrg, tag[key], arr)
          : findPathToScope(selectedOrg, tag[key]);
      let scopes = scopeDetails.map(scope => scope?.name);      
      if (scopes?.length) {
        switch (lastKeyType) {
          case "projectId":
            if (!countAncestors) {
              acc.projectCount += 1;
            }
            scopeDetailsPageRow.push(
              createScopeDetail("project", tag[key], resourceId, scopes, scopeDetails)
            );
            break;
          case "folderId":
            if (!countAncestors) {
              acc.folderCount += 1;
            }
            scopeDetailsPageRow.push(
              createScopeDetail("folder", tag[key], resourceId, scopes, scopeDetails)
            );
            break;
          case "organizationId":
            if (!countAncestors) {
              acc.organizationCount += 1;
            }
            scopeDetailsPageRow.push(
              createScopeDetail("organization", tag[key], resourceId, scopes, scopeDetails)
            );
            break;
          default:
            break;
        }
      }
      return acc;
    },
    { projectCount: 0, folderCount: 0, organizationCount: 0 }
  );

  const { projectCount, folderCount, organizationCount } = count;
  return {
    count: projectCount + folderCount + organizationCount,
    scopeDetailsPageData: scopeDetailsPageRow,
    scopeCount: { projectCount, folderCount, organizationCount },
  };
};

export const getLastKeyAfterColon = (key) => {
  const parts = key.split(":");
  return parts[parts.length - 1];
};

const findScopesForTypeOrg = (selectedOrg, scopeId, arr = []) => {
  if (selectedOrg.id === scopeId) {
    arr.push({ id: selectedOrg.id, name: selectedOrg.name, rules: selectedOrg.rules, type: selectedOrg.resourceType });
  }
  return arr;
};

export const getResourcesListQuery = ({pageSize, sortState, filterState, pageIndex, externalfilters = null, defaultFilter = null, reloadGridData = null, t}) => {
  const gridSearch = filterState?.textFilter;
  const query = {
    limit: pageSize,
    skip: pageSize * pageIndex,
  };  
  if (sortState && sortState.column && sortState.sortOrder) {
    query.orderBy = getOrderByQuery(sortState);
  }
  if(reloadGridData) {
    query.scopeCount = reloadGridData;
  } 
  if (externalfilters) {
    const hasNameRe = /name re/i.test(externalfilters);
    const hasScopeFolderId = /tags.internal:bxp:folderId/i.test(externalfilters);
    const hasScopeProjectId = /tags.internal:bxp:projectId/i.test(externalfilters);
    const hasScopeOrganizationId = /tags.internal:bxp:organizationId/i.test(externalfilters);

    let filterParts = [`(${externalfilters})`];
    if (gridSearch) {
      filterParts.push(`name re '(?i).*${gridSearch}.*'`);
    }
    if (hasNameRe || hasScopeFolderId || hasScopeProjectId || hasScopeOrganizationId) {
      filterParts.push(`(${defaultFilter})`);
    }
    query.filter = filterParts.join(' and ');
  } else {  
    const columnFilter = getColumnFilter(filterState, gridSearch, defaultFilter,t);
    if (columnFilter) {
      query.filter = columnFilter;
    }
  }
  return query;
};

const getOrderByQuery = (sortState) => {
  return `${sortState.column} ${sortState.sortOrder}`;
}

/* 
*  This function is used to get the query for server side filter and grid column filter
*/
export const getColumnFilter = (filterState, gridSearch = null, defaultFilter = null, t) => {
  const filterCriterias = []; 
  let filterCnt = 0;
  if (filterState && !_.isNull(filterState.columns)) {
    _.forEach(filterState.columns, (data, key) => {
      if (key === "platform" || key === "resource") {
        const capitalizedKey =
          _.capitalize(key) + (key === "resource" ? "s" : "");
        const filterCriteria =  processFilterOptions(capitalizedKey, data, t)
        if (filterCriteria.length > 0) {
          filterCriterias.push(processFilterOptions(capitalizedKey, data, t));
        }
      }
    });
  }
  let filter = "";
  if (gridSearch) {
    filterCnt += 1
    filter = `name re '(?i).*${gridSearch}.*'`;
  }
  if (filterCriterias?.length > 0) {
    filter += filterCnt > 0 ? ` and (${filterCriterias.join(") and (")})` : ` ${filterCriterias.join(") and (")}`;
  } else {
    filter += filterCnt > 0 ? ` and (${defaultFilter})` : defaultFilter;
  }
  return filter;
};

/*
* This function is used to process the filter query options
*/
const processFilterOptions = (filterName, filterData,t) => {
  let filter = "";
  _.each(filterData?.values, (_, filterKey) => {
    const queryOptions = getAdvanceFilterOptions(null, t)
      ?.flatMap((option) => (option.name === filterName ? option.options : []))
      .find((option) => option?.value === filterKey)?.Query;
    const queryOptionString = queryOptions?.join(" or ");
    filter = concatenateFilterOptions(
      filter,
      queryOptionString
    );
  });
  return filter;
};

const concatenateFilterOptions = (existingFilter, newFilterOption) =>
  existingFilter ? `${existingFilter} or ${newFilterOption}` : newFilterOption;

const getBackupResourceOptions = (t) => [
  {
    value: "microsoft-sql-server-host",
    label: t("microsoft_sql_server_host"),
    Query: ['resourceType eq "BR_MSSQL_HOST"'],
  },
  {
    value: "oracle-host",
    label: t("oracle_database_host"),
    Query: ['resourceType eq "BR_ORACLE_HOST"'],
  },
  {
    value: "vmware-vcenter",
    label: t("vmware_vcenter"),
    Query: ['resourceType eq "BR_VMWARE_VCENTER"'],
  },
  {
    value: "microsoft-hyper-v-host",
    label: t("microsoft_hyper_v_host"),
    Query: ['resourceType eq "BR_HYPERV_HOST"'],
  },
  {
    value: "kvm-host",
    label: t("kvm_host"),
    Query: ['resourceType eq "BR_KVM_HOST"'],
  },
  {
    value: "kubernetes-cluster",
    label: t("kubernetes_cluster"),
    Query: ['resourceType eq "BR_K8S_CLUSTER"'],
  },
   {
    value: "kubernetes-namespace",
    label: t("kubernetes_namespace"),
    Query: ['resourceType eq "BR_K8S_NAMESPACE"'],
  },
  {
    value: "on-premises-ontap-svm",
    label: t("on_premises_ontap_svm"),
    Query: ['resourceType eq "BR_VOLUMES_SVM"'],
  },
];

export const getAdvanceFilterOptions = (selectedOrg = null, t) => {
  // NCL mode: only show On-premises ONTAP cluster filter
  if (isNcl()) {
    return [
      {
        name: "Platform",
        localizedLabel: t("platform"),
        options: [
          {
            value: "onprem",
            label: t("on_prem_ontap_cluster"),
            Query: [
              'resourceType eq "ON_PREM"',
            ],
          },
          {
            value: "eseries",
            label: t("e_series"),
            Query: [
              'resourceType eq "ESERIES"',
            ],
          },
          {
            value: "sg",
            label: t("storage_grid"),
            Query: [
              'resourceType eq "SG"',
            ],
          },
        ],
        type: "select",
        isMulti: true,
      },
      {
        name: "Resources",
        localizedLabel: t("resources"),
        options: [
          {
            value: "onprem",
            label: t("on_prem_ontap_cluster"),
            Query: [
              'resourceType eq "ON_PREM"',
            ],
          },
          {
            value: "eseries",
            label: t("e_series"),
            Query: [
              'resourceType eq "ESERIES"',
            ],
          },
          {
            value: "sg",
            label: t("storage_grid"),
            Query: [
              'resourceType eq "SG"',
            ],
          },
          ...getBackupResourceOptions(t),
        ],
        type: "select",
        isMulti: true,
      },
      {
        name: "Scope",
        options: [selectedOrg],
        type: "treeSelect",
      },
    ];
  }

  return [
  {
    name: "Platform",
    localizedLabel: t("platform"),
    options: [
      {
        value: "onprem",
        label: t("on_prem_ontap_cluster"),
        Query: [
          ...(showAIQInventory ? [AIQqueryOnPrem] : []),
          'resourceType eq "ON_PREM"',
          'resourceType eq "onpremClusterDirect"',
          'resourceType eq "ESERIES"',
          'resourceType eq "SG"',
          backupOnpremQuery,
          '(resourceClass eq "STORAGE_SERVICES" and (tags.internal:bxp:metadata:location eq "onPrem" or resourceType eq "SG" or resourceType eq "ESERIES"))',
        ],
      },
      {
        value: "AWS",
        label: t("amazon_web_services"),
        Query: [
          ...(showAIQInventory ? [AIQqueryAWS] : []),
          'resourceType eq "AWS"',
          'resourceType eq "AWSHA"',
          backupAWSQuery,
          '(resourceClass eq "STORAGE_SERVICES" and (tags.internal:bxp:metadata:location eq "AWS" or resourceType eq "MSSQL" or resourceType eq "POSTGRESQL"))',
        ],
      },
      {
        value: "gcp",
        label: t("google_cloud"),
        Query: [
          ...(showAIQInventory ? [AIQqueryGCP] : []),
          'resourceType eq "GCP"',
          'resourceType eq "GCPHA"',
          'resourceClass eq "CVS"',
          backupGcpQuery,
        ],
      },
      {
        value: "azure",
        label: t("microsoft_azure"),
        Query: [
          ...(showAIQInventory ? [AIQqueryAzure] : []),
          'resourceClass eq "ANF"',
          'resourceType eq "AZURE"',
          'resourceType eq "AZUREHA"',
          backupAzureQuery
        ],
      },
      {
        value: "oci",
        label: t("oracle_cloud"),
        Query: [
          '(resourceClass eq "ocisn" and resourceType eq "cluster")',
        ],
      },
      {
        value: "keystone",
        label: t("keystone"),
        Query: [
          ...(showAIQInventory ? [AIQqueryKeystone] : [])
        ],
      }
    ],
    type: "select",
    isMulti: true,
  },
  {
      name: "Resources",
      localizedLabel: t("resources"),
      options: [
          {
              value: "cvo",
              label: t("cloud_volumes_ontap"),
              Query: [
                  ...(showAIQInventory ? [`(${AIQinventory} and tags.internal:bxp:variantType eq "CVO")`] : []),
                  '(resourceClass eq "VSA" and (resourceType eq "AZURE" or resourceType eq "GCP" or resourceType eq "AWS"))',
              ],
          },
          {
              value: "cvoha",
              label: t("cloud_volumes_ha_pair"),
              Query: [
                ...(showAIQInventory ? [`(${AIQinventory} and tags.internal:bxp:variantType eq "CVOHA")`] : []),
                  '(resourceClass eq "VSA" and (resourceType eq "AZUREHA" or resourceType eq "GCPHA" or resourceType eq "AWSHA"))',
              ],
          },
          {
              value: "sg",
              label: t("storage_grid"),
              Query: [
                ...(showAIQInventory ? [`(${AIQinventory} and tags.internal:bxp:variantType eq "STORAGEGRID")`] : []),
                'resourceType eq "SG"'
              ],
          },
          {
              value: "eseries",
              label: t("e_series"),
              Query: [
                ...(showAIQInventory ? [`(${AIQinventory} and tags.internal:bxp:variantType eq "E-SERIES")`] : []),
                'resourceType eq "ESERIES"'
              ],
          },
          {
              value: "onprem",
              label: t("on_prem_ontap_cluster"),
              Query: [
                ...(showAIQInventory ? [`(${AIQinventory} and tags.internal:bxp:variantType eq "ONTAP")`] : []),
                  'resourceType eq "ON_PREM"',
                  'resourceType eq "onpremClusterDirect"',
              ],
          },
          {
              value: "anf",
              label: t("azure_net_files"),
              Query: ['resourceClass eq "ANF"'],
          },
          {
              value: "oci-sn",
              label: t("oracle_cloud"),
              Query: [
                '(resourceClass eq "ocisn" and resourceType eq "cluster")',
              ],
          },
          ...(showAIQInventory ? [
            {
              value: "altavault",
              label: t("altaVault"),
              Query: [
                `(${AIQinventory} and tags.internal:bxp:variantType eq "ALTAVAULT")`
              ],
            },
            {
              value: "astra",
              label: t("astra"),
              Query: [
                `(${AIQinventory} and tags.internal:bxp:variantType eq "ASTRA")`
              ],
            },
            {
              value: "cvs",
              label: t("cloud_volumes_service"),
              Query: [
                `(${AIQinventory} and tags.internal:bxp:variantType eq "CVS")`
              ],
            },
            {
              value: "hci",
              label: t("hci"),
              Query: [
                `(${AIQinventory} and tags.internal:bxp:variantType eq "HCI")`
              ],
            },
            {
              value: "ontap-edge",
              label: t("ontap_edge"),
              Query: [
                `(${AIQinventory} and tags.internal:bxp:variantType eq "ONTAP-EDGE")`
              ],
            },
            {
              value: "ontap-select",
              label: t("ontap_select"),
              Query: [
                `(${AIQinventory} and tags.internal:bxp:variantType eq "ONTAP-SELECT")`
              ],
            },
            {
              value: "solidfire",
              label: t("solid_fire"),
              Query: [
                `(${AIQinventory} and tags.internal:bxp:variantType eq "SOLIDFIRE")`
              ],
            },
            {
              value: "subscription",
              label: t("subscription"),
              Query: ['resourceType eq "subscription"'],
            },
            ...getBackupResourceOptions(t),

      ] : [])
      ],
      type: "select",
      isMulti: true,
  },
  {
      name: "Scope",
      options: [selectedOrg],
      type: "treeSelect",
  },
];
};

export const filterOptionsGridColumn = t => getAdvanceFilterOptions(null, t).reduce(
  (acc, item) => {
    if (item.name === "Platform") {
      acc.platform = item.options;
    }
    if (item.name === "Resources") {
      acc.resource = item.options;
    }
    return acc;
  },
  { platform: [], resource: [] }
);

export const schema = yup.array().of(
  yup.object().shape({
    id: yup.number(),
    scopeid: yup.string().required(),
    name: yup.string(),
    resourceType: yup.string().required(),
  })
).test(value => value.length > 0);

export const getNoResourceMessage = (filterState, externalfilters, t) => {
  const gridFilterKeys = Object.keys(filterState?.columns || {});
  return (
    <>
      <ResourceIcon style={{ height: '24px', width: '24px' }} />
      {externalfilters || gridFilterKeys.length > 1 || filterState?.textFilter ? (
        <span>{t('no_resources')}</span>
      ) : (
        <>
          <span>{t('you_dont_have_res')}</span>
          <span>{t('add_res_via')}<Link className={styles["orgs-grid-link"]}  to="/working-environments">{t('canvas')}</Link>
          {process.env.REACT_APP_IS_DARK !== "true" && <>{t('or_discover_via')}<Link className={styles["orgs-grid-link"]} to="/working-environments/my-estate">{t('my_estate')}</Link></>}
          </span>
        </>
      )}
    </>
  );
};

export const getAllocateResMessage = (t) => {
  return (
    <>
      <ResourceIcon style={{ height: '24px', width: '24px' }} />
        <span>{t('no_resources')}</span>
    </>
  );
};

export const platformNameMap = (t) => {
  return {
  ONPREM: t('on_prem_ontap_cluster'),
  AWS: t('amazon_web_services'),
  GCP: t('google_cloud'),
  AZURE: t('microsoft_azure'),
  OCI: t('oracle_cloud'),
  ESERIES: t('e_series'),
  STORAGEGRID: t('storage_grid'),
  KEYSTONE: t('keystone')
  }
}
